/**
 * ActionMenu — overflow menu (3-dot), DropdownMenu wrapper with icon support
 *
 * @example
 * <ActionMenu
 *   items={[
 *     { label: t('admin.btn_edit'), icon: Pencil, onClick: () => handleEdit(row) },
 *     { label: t('admin.btn_delete'), icon: Trash2, onClick: () => handleDelete(row), variant: 'danger' },
 *   ]}
 * />
 */

import PropTypes from 'prop-types';
import { MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';

export default function ActionMenu({ items, triggerClassName, align }) {
  const { t } = useLanguage();

  if (!items || items.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            'h-8 w-8 rounded-lg hover:bg-[var(--bg-app)] text-[var(--text-muted)]',
            triggerClassName,
          )}
          aria-label={t('admin.action_menu_label')}
        >
          <MoreHorizontal size={18} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        className="min-w-[150px] rounded-xl border-2 border-[var(--border-light)] shadow-[0_4px_0_0_var(--border-light)] p-1"
      >
        {items.map((item, i) => {
          if (item.separator) {
            return <DropdownMenuSeparator key={i} className="my-1" />;
          }
          const Icon = item.icon;
          return (
            <DropdownMenuItem
              key={i}
              onClick={item.onClick}
              disabled={item.disabled}
              className={cn(
                'rounded-lg font-semibold text-sm cursor-pointer gap-2 px-3 py-2',
                item.variant === 'danger'
                  ? 'text-[var(--brand-danger)] focus:bg-red-50 focus:text-[var(--brand-danger)]'
                  : 'text-[var(--text-main)] focus:bg-[var(--bg-app)]',
              )}
            >
              {Icon && <Icon size={15} className="flex-shrink-0" />}
              {item.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

ActionMenu.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      icon: PropTypes.elementType,
      onClick: PropTypes.func,
      disabled: PropTypes.bool,
      variant: PropTypes.oneOf(['default', 'danger']),
      /** Render a separator instead of an item */
      separator: PropTypes.bool,
    }),
  ).isRequired,
  triggerClassName: PropTypes.string,
  /** Dropdown alignment relative to trigger */
  align: PropTypes.oneOf(['start', 'center', 'end']),
};

ActionMenu.defaultProps = {
  align: 'end',
};
